// src/hooks/useRecording.ts
import { useState, useEffect, useCallback } from "react";
import { useReactMediaRecorder } from "react-media-recorder";

interface UseRecordingOptions {
  maxLength: number | null;
}

export function useRecording({ maxLength }: UseRecordingOptions) {
  const [recordingTime, setRecordingTime] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(maxLength ?? 0);

  const {
    status,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl,
  } = useReactMediaRecorder({
    audio: true,
    video: false,
    blobPropertyBag: { type: "audio/mpeg" },
  });

  // Update time remaining
  useEffect(() => {
    if (maxLength === null) return;

    let interval: NodeJS.Timeout;
    if (status === "recording" && startTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        const totalRecorded = recordingTime + elapsed;
        setTimeRemaining(maxLength - totalRecorded);
      }, 100);
    } else {
      setTimeRemaining(maxLength - recordingTime);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [status, startTime, recordingTime, maxLength]);

  const handleStop = useCallback(() => {
    if (startTime) {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setRecordingTime((prev) => prev + elapsed);
    }
    stopRecording();
    setStartTime(null);
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
  }, [startTime, stopRecording, timer]);

  // Auto-start timer when recording begins
  useEffect(() => {
    if (status === "recording" && !startTime) {
      setRecordingTime(0);
      const now = Date.now();
      setStartTime(now);

      if (maxLength) {
        const newTimer = setTimeout(() => {
          handleStop();
        }, maxLength * 1000);
        setTimer(newTimer);
      }
    }
  }, [status, startTime, maxLength, handleStop]);

  const handleStart = () => {
    try {
      startRecording();
    } catch (error) {
      console.error("Failed to start recording:", error);
    }
  };

  const handlePause = () => {
    if (startTime) {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setRecordingTime((prev) => prev + elapsed);
    }
    pauseRecording();
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
  };

  const handleResume = () => {
    const now = Date.now();
    setStartTime(now);
    resumeRecording();

    if (maxLength) {
      const remainingTime = maxLength - recordingTime;
      if (remainingTime > 0) {
        const newTimer = setTimeout(() => {
          handleStop();
        }, remainingTime * 1000);
        setTimer(newTimer);
      } else {
        handleStop();
      }
    }
  };

  const handleReset = () => {
    clearBlobUrl();
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
    setStartTime(null);
    setRecordingTime(0);
  };

  return {
    status,
    mediaBlobUrl: mediaBlobUrl ?? null,
    timeRemaining,
    maxLength,
    start: handleStart,
    pause: handlePause,
    resume: handleResume,
    stop: handleStop,
    reset: handleReset,
  };
}
